import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import { BASE_IMG_URL } from '@images/imageUrls';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { copy } from '@utils/copy';

const APP_DOWNLOAD_URL = 'https://m.malink.cn/s/ABbE73';
const DEEP_LINK_DELAY_MS = 100;
const APP_FALLBACK_DELAY_MS = 2000;

interface H5DownloadAppProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    closeAppDownloadEnter?: Function
}
interface H5DownloadAppState {
    isJumpModal: boolean;
    openStatus: string;
}
type H5DownloadAppAllProps = H5DownloadAppProps & RouteComponentProps;
@inject('app', 'site', 'user')
@observer
class H5DownloadApp extends React.Component<H5DownloadAppAllProps, H5DownloadAppState> {
    private deepLinkTimer: number | null = null;
    private fallbackTimer: number | null = null;
    private openAttemptId = 0;
    private isOpeningApp = false;
    private isUnmounted = false;

    state: H5DownloadAppState = {
        isJumpModal: false,
        openStatus: '打开APP',
    };

    componentDidMount() {
        this.isUnmounted = false;
        window.addEventListener('pagehide', this.handlePageHide);
        window.addEventListener('blur', this.handleWindowBlur);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    componentWillUnmount() {
        this.isUnmounted = true;
        window.removeEventListener('pagehide', this.handlePageHide);
        window.removeEventListener('blur', this.handleWindowBlur);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.handlePageHide();
    }

    clearOpenTimers = () => {
        if (this.deepLinkTimer !== null) {
            clearTimeout(this.deepLinkTimer);
            this.deepLinkTimer = null;
        }
        if (this.fallbackTimer !== null) {
            clearTimeout(this.fallbackTimer);
            this.fallbackTimer = null;
        }
    };

    getPageState = () => ({
        hidden: document.hidden,
        hasFocus: document.hasFocus(),
    });

    setOpenStatus = (status: string) => {
        const { hidden, hasFocus } = this.getPageState();
        const statusText = `${status} H${hidden ? 1 : 0}F${hasFocus ? 1 : 0}`;
        console.log('[打开APP状态]', statusText, {
            hidden,
            hasFocus,
            isOpeningApp: this.isOpeningApp,
            attemptId: this.openAttemptId,
        });
        if (!this.isUnmounted) {
            this.setState({ openStatus: statusText });
        }
    };

    finishOpenAttempt = (status = '已离开') => {
        const wasOpeningApp = this.isOpeningApp;
        this.clearOpenTimers();
        this.openAttemptId += 1;
        this.isOpeningApp = false;
        if (wasOpeningApp) {
            this.setOpenStatus(status);
        }
    };

    handlePageHide = () => {
        this.finishOpenAttempt('已离开');
    };

    handleVisibilityChange = () => {
        if (document.hidden) {
            this.finishOpenAttempt('已隐藏');
        }
    };

    handleWindowBlur = () => {
        if (this.isOpeningApp) {
            this.finishOpenAttempt('失焦');
        }
    };

    copyCurrentPageUrl = () => {
        if (this.isVivoBrowser()) return;
        copy(window.location.href);
    };

    isWeixin() {
        const ua = navigator.userAgent.toLowerCase();
        return ua.indexOf('micromessenger') !== -1;
    }

    isVivoBrowser() {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('vivobrowser');
    }

    // 根据当前页面路径计算深链接
    calcDeepLink(): string | null {
        const { pathname, search } = this.props.location;
        const queryParams = new URLSearchParams(search);
        const timestamp = Date.now();

        // 帖子详情页 /threadDetail/:id
        const threadDetailMatch = pathname.match(/\/threadDetail\/(\d+)/);
        // 商品详情页 /goodsDetail/:code，使用当前路由中的商品 code。
        const goodsDetailMatch = pathname.match(/^\/goodsDetail\/([^/]+)\/?$/);
        // 个人主页 /user/:code
        const userMatch = pathname.match(/\/user\/([^/]+)/);
        // 众测详情页 /productDetail/:code
        const productDetailMatch = pathname.match(/\/productDetail\/(\d+)/);
        // 众测列表页 /productList
        const productListMatch = pathname === '/productList';
        // 反馈列表页 /feedbackList
        const feedbackListMatch = pathname === '/feedbackList';
        // V 币明细页：顶部入口跳转 App 首页指定位置。
        const vCoinRecordMatch = pathname === '/vCoinRecord';
        // 奖品管理：顶部入口跳转 App 首页指定位置。
        const prizeManagementMatch = pathname === '/prizeManagement';
        // 发布反馈页 /setFeedbackPost
        const setFeedbackPostMatch = pathname === '/setFeedbackPost';
        // 抽奖详情页 /lottery/:code
        const lotteryDetailMatch = pathname.match(/^\/lottery\/([^/]+)/);
        // 摄影大赛详情页 /photography/:code
        const photographyMatch = pathname.match(/^\/photography\/(\d+)/);
        // 摄影展详情页 /photoExhibition/:id
        const photoExhibitionMatch = pathname.match(/\/photoExhibition\/(\d+)/);
        const carnivalMatch = pathname.match(/^\/h5\/([^/]+)/);

        // 帖子详情页：跳转 APP 帖子详情
        if (threadDetailMatch) {
            const id = threadDetailMatch[1];
            return `vivoclub://com.vivo.club/deep?page=thread_detail&id=${id}&t=${timestamp}`;
        // 商品详情页：跳转 APP 内对应商品详情。
        } else if (goodsDetailMatch) {
            const goodsCode = decodeURIComponent(goodsDetailMatch[1]);
            return `vivoclub://com.vivo.club/deep?page=good_detail&code=${encodeURIComponent(goodsCode)}`;
        // 用户主页：跳转 APP 用户主页
        } else if (userMatch) {
            const code = userMatch[1];
            return `vivoclub://com.vivo.club/deep?page=page_user_detail&code=${encodeURIComponent(code)}&t=${timestamp}`;
        // 众测详情页：跳转 APP 众测详情
        } else if (productDetailMatch) {
            const code = productDetailMatch[1];
            const titleName = queryParams.get('title_name') || document.title || '';
            return `vivoclub://com.vivo.club/deep?page=page_product_crowd_test_detail&code=${code}&title_name=${encodeURIComponent(titleName)}&t=${timestamp}`;
        // 众测列表页：跳转 APP 众测入口
        } else if (productListMatch) {
            return `vivoclub://com.vivo.club/deep?page=product_crowdtest_entrance&t=${timestamp}`;
        // 问题反馈列表页：跳转 APP 反馈列表
        } else if (feedbackListMatch) {
            return 'vivoclub://com.vivo.club/deep?page=problem_feedback';
        // V 币明细页顶部入口：跳转 App 的 V 币明细页面。
        } else if (vCoinRecordMatch) {
            return 'vivoclub://com.vivo.club/deep?page=v_coin_log';
        // 奖品管理页：跳转 App 首页并定位到 index=2。
        } else if (prizeManagementMatch) {
            return 'vivoclub://com.vivo.club/deep?page=start&index=2';
        // 发布反馈页：跳转 APP 发布反馈页
        } else if (setFeedbackPostMatch) {
            return 'vivoclub://com.vivo.club/deep?page=thread_publish&type_publish=2&make_sure=false';
        // 抽奖详情页：跳转 APP 抽奖详情页
        } else if (lotteryDetailMatch) {
            const code = lotteryDetailMatch[1];
            return `vivoclub://com.vivo.club/deep?page=page_lottery_detail&code=${code}&action=H5openLotteryApp`;
        // 摄影大赛页：有作品时跳作品详情，否则跳大赛详情
        } else if (photographyMatch) {
            const contestId = photographyMatch[1];
            const { site } = this.props;
            const currentWorkId = site?.currentPhotographyWorkId;
            if (currentWorkId) {
                return `vivoclub://com.vivo.club/deep?page=photography_compositon_detail&id=${currentWorkId}&t=${timestamp}`;
            } else {
                return `vivoclub://com.vivo.club/deep?page=photography_detail_page&code=${contestId}&t=${timestamp}`;
            }
        // 摄影展详情页：跳转 APP 摄影展详情
        } else if (photoExhibitionMatch) {
            const id = photoExhibitionMatch[1];
            const titleName = queryParams.get('title_name') || document.title || '';
            return `vivoclub://com.vivo.club/deep?page=page_photography_film_exhibition&detail_id=${id}&title_name=${encodeURIComponent(titleName)}&t=${timestamp}`;
        } else if (carnivalMatch) {
            const code = carnivalMatch[1];
            return `vivoclub://com.vivo.club/deep?page=carnival&code=${encodeURIComponent(code)}`;
        }

        return null;
    }

    // 处理下载APP点击事件
    handleDownloadAppClick = () => {
        if (this.isWeixin()) {
            this.copyCurrentPageUrl();
            this.setOpenStatus('微信提示');
            this.setState({ isJumpModal: true });
            return;
        }
        this.copyCurrentPageUrl();
        const deepLink = this.calcDeepLink();
        const targetLink = deepLink || 'vivoclub://com.vivo.club/deep?page=start';
        this.clearOpenTimers();
        const attemptId = ++this.openAttemptId;
        this.isOpeningApp = true;
        this.setOpenStatus('准备唤起');

        // 发起 deeplink；是否成功以页面是否离开/隐藏为准。
        this.deepLinkTimer = window.setTimeout(() => {
            if (attemptId !== this.openAttemptId) return;
            this.deepLinkTimer = null;
            this.setOpenStatus('已发起');
            window.location.href = targetLink;
        }, DEEP_LINK_DELAY_MS);

        // 5 秒后仍未离开当前页面，认为 deeplink 未唤起成功，跳应用商店。
        // pagehide/visibilitychange/blur 会提前清理这个定时器，避免 App 已打开后再跳商店。
        this.fallbackTimer = window.setTimeout(() => {
            if (attemptId !== this.openAttemptId || !this.isOpeningApp) return;
            this.fallbackTimer = null;
            this.setOpenStatus('将跳商店');
            window.location.href = APP_DOWNLOAD_URL;
        }, APP_FALLBACK_DELAY_MS);
    }

    render() {
        const { site, app, closeAppDownloadEnter, ...props } = this.props;
        const { isJumpModal, openStatus } = this.state;
        return <>
            <div className={styles.downloadBox}>
                <img  src={`${BASE_IMG_URL}/common/app_logo.png`} onClick={()=> {
                    this.props.history.push(`/`);
                }} alt='logo' className={styles.appLogo} />
                <div onClick={()=> {
                    this.props.history.push(`/`);
                }} className={styles.downloadTitle}>vivo社区<span>与热爱同行</span></div>
                <div className={styles.downloadBtn} title={openStatus} onClick={this.handleDownloadAppClick}>打开APP</div>
                <img  src={`${BASE_IMG_URL}/common/close.png`} alt='' className={styles.closeIcon} onClick={()=> {
                    closeAppDownloadEnter && closeAppDownloadEnter()
                }}/>
            </div>
            {isJumpModal && (
                <div className={styles.opAlertTable} onClick={() => this.setState({ isJumpModal: false })}>
                    <div className={styles.alertWhite}>
                        <div className={styles.inter}></div>
                        点击右上角选择在默认浏览器中打开
                    </div>
                </div>
            )}
        </>
        
    }
}

export default withRouter(H5DownloadApp);
